// Client-side crop/rotate helpers for the guide image uploader.

// Final output size: the app shows guide images at most ~1200px wide (≈400pt @3x).
// The browser does all optimization; the server stores the file as-is.
const MAX_OUTPUT_DIMENSION = 1200;
const JPEG_QUALITY = 0.8;

export const readFileAsDataURL = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });

const loadImage = (src) =>
    new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = src;
    });

const toRadians = (deg) => (deg * Math.PI) / 180;

// Bounding box of a width×height rectangle rotated by `rotation` degrees.
const rotatedSize = (width, height, rotation) => {
    const r = toRadians(rotation);
    return {
        width: Math.abs(Math.cos(r) * width) + Math.abs(Math.sin(r) * height),
        height: Math.abs(Math.sin(r) * width) + Math.abs(Math.cos(r) * height),
    };
};

/**
 * Crops `src` to `pixelCrop` (from react-easy-crop's onCropComplete, in rotated-image
 * coordinates) and returns a compressed JPEG File, downscaled so neither side
 * exceeds MAX_OUTPUT_DIMENSION. Re-encoding through canvas also drops EXIF/GPS metadata.
 */
export const getCroppedFile = async (src, pixelCrop, rotation = 0, fileName = 'image.jpg') => {
    const image = await loadImage(src);

    // 1. Draw the whole image rotated onto a canvas big enough to hold it
    const { width: bw, height: bh } = rotatedSize(image.width, image.height, rotation);
    const rotatedCanvas = document.createElement('canvas');
    rotatedCanvas.width = bw;
    rotatedCanvas.height = bh;
    const rctx = rotatedCanvas.getContext('2d');
    rctx.translate(bw / 2, bh / 2);
    rctx.rotate(toRadians(rotation));
    rctx.drawImage(image, -image.width / 2, -image.height / 2);

    // 2. Copy the crop area out, scaling down if it is very large
    const scale = Math.min(1, MAX_OUTPUT_DIMENSION / Math.max(pixelCrop.width, pixelCrop.height));
    const out = document.createElement('canvas');
    out.width = Math.round(pixelCrop.width * scale);
    out.height = Math.round(pixelCrop.height * scale);
    const octx = out.getContext('2d');
    octx.imageSmoothingQuality = 'high';
    // White background so transparent PNGs don't turn black in JPEG
    octx.fillStyle = '#fff';
    octx.fillRect(0, 0, out.width, out.height);
    octx.drawImage(
        rotatedCanvas,
        pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height,
        0, 0, out.width, out.height
    );

    const blob = await new Promise((resolve) => out.toBlob(resolve, 'image/jpeg', JPEG_QUALITY));
    const name = fileName.replace(/\.[^.]+$/, '') + '.jpg';
    return new File([blob], name, { type: 'image/jpeg' });
};
